import {
  applyPhoneNumberMask,
  getPhoneDigitsForMaskInput,
} from "@plasma/utils/phone-numbers";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { locationsCreate } from "~/api-refs";
import {
  type Location,
  LocationAddressField,
  LocationBusinessHoursField,
  LocationLogoField,
  LocationNameField,
  LocationPhoneNumberField,
  LocationStyleAndTypeField,
} from "~/components/locations/location-form-fields";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute("/_authenticated/locations/new")({
  component: CreateLocationModal,
});

type Step1Data = Pick<Location, "logoUrl" | "name" | "type" | "style">;
type Step2Data = Pick<Location, "address" | "phoneNumber">;
type Step3Data = Pick<
  Location,
  "businessHours" | "operationalStatus" | "closeDateRange"
>;

function CreateLocationModal() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1Data | undefined>();
  const [step2Data, setStep2Data] = useState<Step2Data | undefined>();

  const handleClose = () => {
    void navigate({ to: "/locations" });
  };

  const createLocationMutation = useMutation({
    mutationFn: (data: Step1Data & Step2Data & Step3Data) => {
      return convexClient.mutation(locationsCreate, data);
    },
  });

  const createLocation = async (data: Step1Data & Step2Data & Step3Data) => {
    try {
      const result = await createLocationMutation.mutateAsync(data);
      if (!result.success) {
        logger.error("location_create_failed", { error: result.error });
        return;
      }
      // Track location creation in PostHog.
      if (result.locationId) {
        ProductEvents.locationCreated(result.locationId);
      }
      handleClose();
    } catch (error) {
      logger.exception("location_create_exception", error);
    }
  };

  const handleStep1Submit = (data: Step1Data) => {
    setStep1Data(data);
    setCurrentStep(1);
  };

  const handleStep2Submit = (data: Step2Data) => {
    setStep2Data(data);
    setCurrentStep(2);
  };

  const handleStep3Submit = async (data: Step3Data) => {
    if (!(step1Data && step2Data)) {
      return;
    }
    await createLocation({
      ...step1Data,
      ...step2Data,
      ...data,
    });
  };

  const stepTitles = ["Add location", "Contact details", "Business hours"];
  const stepDescriptions = [
    "Enter the details of your location below",
    "Enter your location contact details below",
    "Enter your location's usual business hours or closures",
  ];

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noCloseButton noScrollPrevention>
        <div className="space-y-5">
          {/* Step indicator */}
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((step) => (
              <button
                className={twJoin(
                  "h-1.5 rounded-full",
                  step <= currentStep ? "bg-primary" : "bg-muted"
                )}
                key={step}
                onClick={
                  step < currentStep ? () => setCurrentStep(step) : undefined
                }
                type="button"
              />
            ))}
          </div>

          <DialogHeader className="space-y-0">
            <DialogTitle className="subtitle font-heading text-foreground text-center">
              {stepTitles[currentStep]}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-center">
              {stepDescriptions[currentStep]}
            </DialogDescription>
          </DialogHeader>

          {currentStep === 0 && (
            <LocationFormStep1 data={step1Data} onSubmit={handleStep1Submit} />
          )}
          {currentStep === 1 && (
            <LocationFormStep2 data={step2Data} onSubmit={handleStep2Submit} />
          )}
          {currentStep === 2 && (
            <LocationFormStep3
              isCreating={createLocationMutation.isPending}
              onSubmit={handleStep3Submit}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LocationFormStep1({
  onSubmit,
  data,
}: {
  onSubmit: (data: Step1Data) => void;
  data?: Step1Data;
}) {
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<Step1Data>({
    defaultValues: data ?? {
      logoUrl: null,
      name: "",
      type: "",
      style: "QSR",
    },
  });

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <LocationLogoField
          control={form.control}
          name="logoUrl"
          setUploading={setIsUploading}
        />
        <LocationNameField control={form.control} name="name" />
        <LocationStyleAndTypeField
          control={form.control}
          resetField={form.resetField}
          styleField="style"
          typeField="type"
          watch={form.watch}
        />
        <Button className="w-full" disabled={isUploading} size="lg">
          {isUploading ? "Uploading image..." : "Next"}
        </Button>
      </form>
    </Form>
  );
}

function LocationFormStep2({
  onSubmit,
  data,
}: {
  onSubmit: (data: Step2Data) => void;
  data?: Step2Data;
}) {
  const form = useForm<Step2Data>({
    defaultValues: {
      address: data?.address ?? {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
      },
      phoneNumber: data ? applyPhoneNumberMask(data.phoneNumber) : "",
    },
  });

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        onSubmit={form.handleSubmit((formData) => {
          const digits = getPhoneDigitsForMaskInput(formData.phoneNumber);
          const cleanedData = {
            ...formData,
            phoneNumber: digits ? `+1${digits}` : "",
          };
          onSubmit(cleanedData);
        })}
      >
        <LocationAddressField
          addressLine1Field="address.addressLine1"
          addressLine2Field="address.addressLine2"
          cityField="address.city"
          control={form.control}
          resetField={form.resetField}
          stateField="address.state"
          zipCodeField="address.zipCode"
        />
        <LocationPhoneNumberField control={form.control} name="phoneNumber" />
        <Button className="w-full" size="lg">
          Next
        </Button>
      </form>
    </Form>
  );
}

function LocationFormStep3({
  onSubmit,
  isCreating,
}: {
  onSubmit: (data: Step3Data) => void;
  isCreating: boolean;
}) {
  const form = useForm<Step3Data>({
    defaultValues: {
      businessHours: [
        { dayOfTheWeek: "Monday", openTime: "8:00am", closeTime: "12:00am" },
        { dayOfTheWeek: "Tuesday", openTime: "8:00am", closeTime: "12:00am" },
        { dayOfTheWeek: "Wednesday", openTime: "8:00am", closeTime: "12:00am" },
        { dayOfTheWeek: "Thursday", openTime: "8:00am", closeTime: "12:00am" },
        { dayOfTheWeek: "Friday", openTime: "8:00am", closeTime: "12:00am" },
        { dayOfTheWeek: "Saturday", openTime: "8:00am", closeTime: "12:00am" },
        { dayOfTheWeek: "Sunday", openTime: "8:00am", closeTime: "12:00am" },
      ],
      operationalStatus: "OPEN",
      closeDateRange: {
        closeStartDate: null,
        closeEndDate: null,
      },
    },
  });

  return (
    <Form {...form}>
      <form
        className="mx-auto flex flex-col items-center gap-5"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <LocationBusinessHoursField
          closeEndDateField="closeDateRange.closeEndDate"
          closeStartDateField="closeDateRange.closeStartDate"
          control={form.control}
          operationalStatusField="operationalStatus"
          resetField={form.resetField}
          watch={form.watch}
        />
        <Button className="w-full" disabled={isCreating} size="lg">
          {isCreating ? "Saving..." : "Create Location"}
        </Button>
      </form>
    </Form>
  );
}
